import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getTaskProgress, isTaskRunning, SLOW_AFTER_MS } from '../utils/extractionProgress';

const STORAGE_KEY_PREFIX = 'extraction-progress-start';

/* Left behind by the phase-based estimator this replaced. Cleared on the way past so a long-lived
   session does not carry its keys forever. */
const LEGACY_PHASE_SUFFIXES = ['fullEstimate', 'headerFooter', 'fundMapping', 'extraction'];

const readSession = key => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSession = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* Private windows and full quotas both throw here. The marker is a nicety — losing it costs an
       elapsed time that starts again from zero, not a broken table. */
  }
};

const clearSession = key => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* see writeSession */
  }
};

/**
 * Live progress for a table of extraction rows.
 *
 * Answers one question per row — how far along is it, and how long has it been going — from what
 * the backend reports, plus a locally remembered "first seen running" that survives a reload and
 * covers the case where the backend sends no start time.
 *
 * @param {Array<object>} records
 * @param {object} options
 * @param {boolean} [options.isActive]   false while the table is closed: no ticking
 * @param {(record: object) => object|null|undefined} options.taskOf
 * @param {number} [options.slowAfterMs]
 */
export const useExtractionProgress = (records, { isActive = true, taskOf, slowAfterMs } = {}) => {
  const [now, setNow] = useState(() => Date.now());
  const observedStartByTaskRef = useRef({});

  const runningTaskIds = useMemo(() => {
    const ids = new Set();
    (records ?? []).forEach(record => {
      const task = taskOf?.(record);
      if (task?.id != null && isTaskRunning(task)) ids.add(String(task.id));
    });
    return ids;
  }, [records, taskOf]);

  /* Recorded as a side effect of rendering rather than in an effect: the first paint of a running
     row should already carry its start, and an effect would give that paint no elapsed time at
     all. Writing only when the key is absent keeps it idempotent under StrictMode's double render. */
  runningTaskIds.forEach(taskId => {
    if (observedStartByTaskRef.current[taskId] != null) return;
    const storageKey = `${STORAGE_KEY_PREFIX}-${taskId}`;
    const stored = Number(readSession(storageKey));
    const startedAt = Number.isFinite(stored) && stored > 0 ? stored : Date.now();
    observedStartByTaskRef.current[taskId] = startedAt;
    writeSession(storageKey, String(startedAt));
  });

  useEffect(() => {
    Object.keys(observedStartByTaskRef.current).forEach(taskId => {
      if (runningTaskIds.has(taskId)) return;
      delete observedStartByTaskRef.current[taskId];
      clearSession(`${STORAGE_KEY_PREFIX}-${taskId}`);
      LEGACY_PHASE_SUFFIXES.forEach(suffix =>
        clearSession(`${STORAGE_KEY_PREFIX}-${taskId}-${suffix}`)
      );
    });
  }, [runningTaskIds]);

  const hasRunningTask = runningTaskIds.size > 0;

  /* Whether anything on the table is still owed an answer — running OR queued. What decides whether
     the view keeps asking; `hasRunningTask` decides whether the clock ticks. */
  /* Anything still owed an answer — running or queued. `isTaskRunning` counts both since dev's
     status refactor added 'queued' to it, so this asks that one rather than keeping a second,
     competing definition of the same thing. */
  const hasPendingTask = useMemo(
    () => (records ?? []).some(record => isTaskRunning(taskOf?.(record))),
    [records, taskOf]
  );

  useEffect(() => {
    if (!isActive || !hasRunningTask) return undefined;
    setNow(Date.now());
    /* Once a second, and only while something is actually running — the elapsed time is the only
       thing that moves between fetches, and it moves in seconds. */
    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [isActive, hasRunningTask]);

  const progressOf = useCallback(
    record => {
      const task = taskOf?.(record);
      const taskId = task?.id == null ? null : String(task.id);
      return getTaskProgress({
        task,
        observedStartMs: taskId ? (observedStartByTaskRef.current[taskId] ?? null) : null,
        nowMs: now,
        slowAfterMs: slowAfterMs ?? SLOW_AFTER_MS
      });
    },
    [taskOf, now, slowAfterMs]
  );

  return { progressOf, hasRunningTask, hasPendingTask, now };
};

export default useExtractionProgress;
